import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Button, Input, Icon, useToast, HStack,
} from '@chakra-ui/react';
import { LuCheck, LuTag } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  fetchCurrencies, updateCurrency, createTag, createAccount, fetchAccounts,
} from '../../api/endpointsBackend';
import { currencyForCountry } from '../../utils/onboardingCountries';
import { onboardingVariant, invitedWorkspace } from '../../utils/onboardingGate';
import AccountForm from '../accounts/AccountForm';
import useIsPhone from '../../hooks/useIsPhone';
import OnboardingCategories from './OnboardingCategories';
import OnboardingFinish from './OnboardingFinish';
import GuestOnboarding from './GuestOnboarding';
import {
  OnboardingFrame, LanguageField, CountryField, CARD_W, usePressGate,
} from './OnboardingShell';

// One step per stop on the rail. The rail is a promise about how long this
// takes, so a stop that passes without the rail moving breaks it: language and
// country are one screen, and so are categories and tags.
// See docs/specs/2026-09-23-full-page-onboarding-design.md.
const STEP_KEYS = ['welcome', 'account', 'personalize', 'done'];

// Invited members get a flow of their own on the same page and shell: three
// stops, and none of them writes to the workspace they were invited to. The
// owner's list is the one above, unchanged. See GuestOnboarding.js.
//
// Someone who already uses Mi Kapital and accepts an invitation gets the guest
// flow without its welcome stop: they chose their language and country long
// ago. The backend says which kind they are (`inviteFirstRun`); a marker
// without the flag is a first-run one.
const STEPS_BY_VARIANT = {
  owner: STEP_KEYS,
  guest: ['welcome', 'role', 'done'],
  returningGuest: ['role', 'done'],
};

// Steps used to be finer-grained. A resumer carrying one of the old keys lands
// on the screen that absorbed it rather than back at the start.
const ABSORBED = {
  language: 'welcome',
  country: 'welcome',
  categories: 'personalize',
  tag: 'personalize',
  summary: 'done',
};

export function stepIndexForKey(key) {
  const i = STEP_KEYS.indexOf(ABSORBED[key] || key);
  return i >= 0 ? i : 0;
}

// The account step is drawn narrower than the rest: it is the only step that
// is a form, and this is the measure the standing "Nueva cuenta" panel uses.
// The page keeps its full width either way, so the rail and the card's left
// edge never move as you advance — only the card's right edge does.
const ACCOUNT_CARD_W = '460px';

function OwnerOnboarding() {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const {
    setLanguage, setCountry, country, setHomeCurrency, ackStep, onboarding,
    markWizardStarted, markWizardCompleted, setOnboardingStep,
    markAccountCreated, markCashAccountSeeded,
  } = usePreferences();
  const toast = useToast();

  const [step, setStep] = useState(() => stepIndexForKey(onboarding?.currentStepKey));
  const [currencies, setCurrencies] = useState([]);
  const [tagName, setTagName] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [tagDone, setTagDone] = useState(false);
  // The account step's submit lives in the card footer, not inside the form —
  // one button for one action. The form hands back its save and its readiness.
  const accountSubmit = useRef(null);
  // The account the step created, shown when the user comes back to it.
  const [createdAccount, setCreatedAccount] = useState(null);
  const [accountReady, setAccountReady] = useState({ canSave: false, saving: false, accent: null });
  const onAccountReady = useCallback((next) => setAccountReady(next), []);

  const key = STEP_KEYS[step];

  useEffect(() => { markWizardStarted(); }, [markWizardStarted]);

  // Remember where they are, so leaving and coming back lands on this screen.
  useEffect(() => { setOnboardingStep(key); }, [key, setOnboardingStep]);

  useEffect(() => {
    fetchCurrencies().then((c) => setCurrencies(c || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (key !== 'account' || !onboarding?.accountCreatedAt) return undefined;
    let cancelled = false;
    fetchAccounts()
      .then((rows) => {
        if (cancelled) return;
        const list = rows || [];
        setCreatedAccount(list.find((a) => a.id === onboarding?.accountId)
          || list.find((a) => a.account_type !== 'cash') || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [key, onboarding?.accountCreatedAt, onboarding?.accountId]);

  const goNext = () => setStep((s) => Math.min(s + 1, STEP_KEYS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // The footer's presses are taken one at a time (see usePressGate).
  const { acceptPress, advancing } = usePressGate(step);
  const handleBack = () => { if (acceptPress()) goBack(); };

  const activateCurrency = async (code) => {
    const cur = currencies.find((c) => c.name === code);
    if (cur && !cur.is_active) {
      try {
        await updateCurrency(cur.id, { is_active: true });
        setCurrencies((prev) => prev.map((c) => (c.id === cur.id ? { ...c, is_active: true } : c)));
      } catch (e) { /* ignore */ }
    }
  };

  const onSelectCountry = async (code) => {
    setCountry(code);
    const cur = currencyForCountry(code);
    if (cur) {
      setHomeCurrency(cur);
      await activateCurrency(cur);
      toast({ description: t('wizard.country.activated', { currency: cur }), status: 'success', duration: 2000, isClosable: true });
    }
  };

  // The cash account, created once the welcome step resolves a currency — an
  // account's currencies can't be empty, so this can't happen at registration
  // where the country isn't known yet. Stamped, so passing through twice never
  // makes a second one.
  const seedCashAccount = useCallback(async () => {
    if (onboarding?.cashAccountAt) return;
    const currency = currencyForCountry(country)
      || currencies.find((c) => c.is_active)?.name;
    if (!currency) return;
    try {
      // The stamp can be stale: a back/forward into this page may be served
      // an old copy of the user's preferences from the browser's cache. A
      // cash account that already exists is the answer, whatever it says.
      const existing = await fetchAccounts();
      if ((existing || []).some((a) => a.account_type === 'cash')) {
        markCashAccountSeeded();
        return;
      }
      await createAccount({
        name: t('cashAccount.name'),
        account_type: 'cash',
        currencies: [currency],
        icon: '💵',
      });
      markCashAccountSeeded();
    } catch (e) { /* a missing cash account is not worth blocking the flow */ }
  }, [onboarding?.cashAccountAt, country, currencies, t, markCashAccountSeeded]);

  const handleCreateTag = async () => {
    if (!tagName.trim()) return;
    setTagBusy(true);
    try {
      await createTag({ name: tagName.trim() });
      setTagDone(true);
      toast({ description: t('wizard.tag.created'), status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ description: e?.response?.data?.detail || 'Error', status: 'error', duration: 3000, isClosable: true });
    } finally { setTagBusy(false); }
  };

  const renderWelcome = () => (
    <>
      <LanguageField onSelect={setLanguage} />
      <CountryField value={country} onChange={onSelectCountry} hint={t('wizard.welcome.optional')} />

      <Text fontSize="13px" color="redesign.text.muted" lineHeight="1.5" mt="-4px" maxW="480px">
        {t('wizard.country.note')}
      </Text>
    </>
  );

  const renderPersonalize = () => (
    <>
      <OnboardingCategories />

      {/* The field sits beside the text where there is room for it, and under
          it where there isn't — a 104px input plus a button next to two lines
          of copy does not fit a phone, and forcing it pushes the whole card
          off-screen. */}
      <Flex
        direction={isPhone ? 'column' : 'row'}
        align={isPhone ? 'stretch' : 'center'}
        gap="14px"
        p="14px"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="16px"
        bg="redesign.bg.surface"
      >
        <Flex align="center" gap="14px" flex="1" minW={0}>
          <Flex
            boxSize="40px"
            borderRadius="12px"
            align="center"
            justify="center"
            flexShrink={0}
            bg="rgba(21, 128, 61, 0.10)"
            _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
            color="redesign.amount.income"
          >
            <Icon as={LuTag} boxSize="18px" strokeWidth={1.7} />
          </Flex>
          <Box flex="1" minW={0}>
            <Text fontSize="14px" fontWeight={600} color="redesign.heading" mb="2px">
              {t('wizard.tag.rowTitle')}
            </Text>
            <Text fontSize="13px" color="redesign.text.muted" lineHeight="1.4">
              {t('wizard.tag.rowDesc')}
            </Text>
          </Box>
        </Flex>
        <HStack spacing="8px" flexShrink={0}>
          <Input
            w={isPhone ? 'full' : '104px'}
            h={isPhone ? '44px' : 'auto'}
            // The default placeholder grey is about 2:1 in dark mode.
            _placeholder={isPhone ? { color: 'phone.muted' } : undefined}
            // Return adds the tag. On the phone it also drops the keyboard,
            // so the action bar under it is back in view.
            enterKeyHint={isPhone ? 'done' : undefined}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (isPhone) e.currentTarget.blur();
              if (!tagBusy) handleCreateTag();
            }}
            px="10px"
            py="7px"
            fontSize="13px"
            borderRadius="9px"
            placeholder={t('wizard.tag.placeholder')}
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            isDisabled={tagDone}
          />
          <Button
            h={isPhone ? '44px' : 'auto'}
            px="12px"
            py="7px"
            fontSize={isPhone ? '16px' : '13px'}
            fontWeight={600}
            borderRadius="9px"
            variant="outline"
            borderColor="redesign.border.strong"
            color="redesign.text"
            bg="redesign.bg.surface"
            _hover={{ bg: 'redesign.bg.hover' }}
            flexShrink={0}
            onClick={handleCreateTag}
            isLoading={tagBusy}
            isDisabled={tagDone || !tagName.trim()}
            leftIcon={tagDone ? <Icon as={LuCheck} boxSize="13px" /> : undefined}
          >
            {tagDone ? t('wizard.tag.added') : t('wizard.tag.add')}
          </Button>
        </HStack>
      </Flex>
    </>
  );

  const renderCreatedAccount = () => (
    <>
      <Flex
        align="center"
        gap="14px"
        p="14px"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="16px"
        bg="redesign.bg.surface"
      >
        <Flex
          boxSize="40px"
          borderRadius="12px"
          align="center"
          justify="center"
          flexShrink={0}
          bg="rgba(21, 128, 61, 0.10)"
          _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
          color="redesign.amount.income"
        >
          <Icon as={LuCheck} boxSize="20px" strokeWidth={2.2} />
        </Flex>
        <Box minW={0}>
          <Text fontSize={isPhone ? '16px' : '14px'} fontWeight={600} color="redesign.heading" noOfLines={1}>
            {createdAccount?.name || t('stages.account')}
          </Text>
          {createdAccount && (
            <Text fontSize="13px" color={isPhone ? 'phone.muted' : 'redesign.text.muted'} noOfLines={1}>
              {[
                createdAccount.source,
                createdAccount.account_type && t(`accounts:types.${createdAccount.account_type}`, { defaultValue: '' }),
                (createdAccount.currencies || []).join(' · '),
              ].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Box>
      </Flex>
      <Text fontSize="13px" color={isPhone ? 'phone.muted' : 'redesign.text.muted'} mt="12px" lineHeight="1.5">
        {t('wizard.account.editLater')}
      </Text>
    </>
  );

  const renderBody = () => {
    switch (key) {
      case 'welcome':
        return renderWelcome();
      case 'account': {
        // Already created (a resumer, or Atrás from a later step): show it,
        // rather than an empty form whose input Continuar would ignore.
        if (onboarding?.accountCreatedAt) return renderCreatedAccount();
        const countryCurrency = currencyForCountry(country);
        return (
          <AccountForm
            embedded
            compact
            noAutoFocus={isPhone}
            submitRef={accountSubmit}
            onReadyChange={onAccountReady}
            account={null}
            initialCurrencies={countryCurrency ? [countryCurrency] : []}
            onSaved={(saved) => { markAccountCreated(saved?.id); goNext(); }}
          />
        );
      }
      case 'personalize':
        return renderPersonalize();
      case 'done':
        return (
          <OnboardingFinish
            onDone={markWizardCompleted}
            onBack={handleBack}
            defaultAccountId={onboarding?.accountId ?? null}
          />
        );
      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (!acceptPress()) return;
    advancing.current = true;
    try {
      if (key === 'welcome') await seedCashAccount();
      // One screen, two tracked steps: both are acknowledged by passing it.
      if (key === 'personalize') { ackStep('categories'); ackStep('tag'); }
      goNext();
    } catch (e) {
      advancing.current = false;
    }
  };

  // The account step is the one thing that must actually happen. Its footer
  // button saves the form — except for a resumer who already did it, whose
  // form is empty and would otherwise never enable the button again.
  const savesTheForm = key === 'account' && !onboarding?.accountCreatedAt;
  const primaryDisabled = savesTheForm && !accountReady.canSave;
  const primaryLabel = t('wizard.continue');
  const onPrimary = savesTheForm
    ? () => { if (acceptPress()) accountSubmit.current?.(); }
    : handleNext;
  const stepDesc = key === 'account' && onboarding?.accountCreatedAt
    ? t('wizard.account.descDone')
    : t(`wizard.${key}.desc`);

  return (
    <OnboardingFrame
      steps={STEP_KEYS}
      step={step}
      cardMaxW={key === 'account' ? ACCOUNT_CARD_W : CARD_W}
      desc={stepDesc}
      onBack={handleBack}
      onSkip={key === 'personalize' ? handleNext : undefined}
      primary={{
        label: primaryLabel,
        onClick: onPrimary,
        isDisabled: primaryDisabled,
        isLoading: savesTheForm && accountReady.saving,
        accent: savesTheForm ? accountReady.accent : null,
      }}
    >
      {renderBody()}
    </OnboardingFrame>
  );
}

/**
 * /onboarding: the owner's flow, or an invited member's.
 *
 * The variant comes from the same gate the layout's redirect uses
 * (utils/onboardingGate.js). Anyone the gate does not call a guest gets the
 * owner flow, as before — including `?onboarding=force` on an account the gate
 * would not hold, which is what that QA affordance has always done.
 *
 * It is decided once, on arrival, and kept. The guest flow's last button
 * stamps `inviteCompletedAt` before it leaves, which turns the gate's answer
 * to null for the moment in between; re-deciding then would swap in the owner
 * flow, whose first effects stamp owner state (`wizardStartedAt`,
 * `currentStepKey`) on someone who is not setting up a space of their own.
 * ProtectedRoute only renders this page once the workspaces are loaded, and
 * preferences hydrate before them, so the first render has what it needs.
 */
export default function OnboardingPage() {
  const { onboarding } = usePreferences();
  const { workspaces, current } = useWorkspace();
  const [invited] = useState(() => (
    onboardingVariant({ onboarding, workspaces, currentWorkspace: current }) === 'guest'
      ? invitedWorkspace({ onboarding, workspaces })
      : null
  ));
  if (invited) {
    return (
      <GuestOnboarding
        steps={onboarding.inviteFirstRun === false
          ? STEPS_BY_VARIANT.returningGuest
          : STEPS_BY_VARIANT.guest}
        workspace={invited}
        // The role held there now; the marker's copy is only what it was at
        // accept time, and the host may have changed it since.
        role={invited.workspace_role || onboarding.inviteRole}
      />
    );
  }
  return <OwnerOnboarding />;
}
